import React from "react";
import gql from "graphql-tag";
import { Query } from "@apollo/client/react/components";

import { client } from "../index";
import LoadingView from "./LoadingView";
import ErrorView from "./ErrorView";
import Image from "react-bootstrap/Image";
import OcrProcessBoxList from "./OcrProcessBoxList";
import OcrProcessTable from "./OcrProcessTable";
import MessageEditorModal from "./MessageEditorModal";
import Button from "react-bootstrap/Button";
import AttachmentLightbox from "./AttachmentLightbox";

import "../styles/OcrProcesses.css";
import "../styles/document_list.css";
import unknown_file from "../images/fileformat_unknown.png";

export interface IOcrProcessLineItem {
  token: string;
  receiptNumber: number;
  receiptClass: string;
  status: number;
  bhStatus: number;
  vuStatus: number;
  behandlungsdatum: string;
  careProvider: string;
  accountHolderName: string;
  tags: [string];
  receiptSum: string;
  receiptDate: string;
  extReceiptNumber: string;
  abrechner: string;
  leistungPkvPpv: string;
  leistungBeihilfe: string;
  eigenanteil: string;
  erstattungSum: string;
  erstattungVu?: string;
  erstattungBh?: string;
  paidOn?: string;
  paid: boolean;
  paidByClient?: boolean;
  notes?: string;
  images?: [
    {
      thumb: string;
      full: string;
    }
  ];
}

export interface OcrProcess {
  createdAt: string;
  state: string;
  sum: number;
  erstattungBh: number;
  erstattungVu: number;
  sew: number;
  processNumber: string;
  submissionNote?: string;
  lineItems?: [IOcrProcessLineItem];
}

interface OcrProcessDisplayProps {
  number: string;
  table?: boolean;
}

export const GET_PROCESS = gql`
  query ocrProcesses($number: String) {
    ocrProcesses(number: $number) {
      processNumber
      state
      sum
      sew
      erstattungBh
      erstattungVu
      createdAt
      submissionNote
      attachments {
        name
        url
        thumbnail
        images {
          thumb
          full
        }
      }
      lineItems {
        token
        abrechner
        behandlungsdatum
        careProvider
        accountHolderName
        eigenanteil
        erstattungSum
        extReceiptNumber
        images {
          thumb
          full
        }
        leistungBeihilfe
        leistungPkvPpv
        notes
        paidByClient
        paidOn
        paid
        receiptClass
        receiptDate
        receiptNumber
        receiptSum
        status
        bhStatus
        vuStatus
        tags
      }
    }
  }
`;

const SEND_TEAM_MESSAGE = gql`
  mutation sendMessageToTeam($subject: String, $message: String) {
    sendMessageToTeam(subject: $subject, message: $message) {
      success
    }
  }
`;

export function statusToClass(status: number) {
  if (status === 0) {
    return "offen";
  }
  if (status === 1) {
    return "eingereicht";
  }
  if (status === 2) {
    return "erstattet";
  }
  if (status === 3) {
    return "einspruch";
  }
  if (status === 4) {
    return "abgeschlossen";
  }

  return "nachforderung";
}

export default class OcrProcessDisplay extends React.Component<OcrProcessDisplayProps> {
  state: {
    messageEditorIsOpen: boolean;
    message?: string;
    subject?: string;
    sendSuccess: boolean;
    sendError: boolean;
  };

  constructor(props: any) {
    super(props);

    this.state = {
      messageEditorIsOpen: false,
      subject: "",
      message: "",
      sendSuccess: false,
      sendError: false,
    };

    this.submitMessage = this.submitMessage.bind(this);
    this.updateMessageText = this.updateMessageText.bind(this);
  }

  updateMessageText(text: string) {
    this.setState({ ...this.state, message: text });
  }

  emailQuestionSubject(processNumber: string) {
    return `Anfrage zu Vorgang ${processNumber}`;
  }

  composeMessage() {
    this.setState({ ...this.state, messageEditorIsOpen: true });
  }

  async submitMessage() {
    const success = await client.mutate({
      mutation: SEND_TEAM_MESSAGE,
      variables: {
        subject: this.state.subject,
        message: this.state.message,
      },
    });

    if (success && success.data.sendMessageToTeam.success === true) {
      this.setState({
        ...this.state,
        subject: undefined,
        message: "",
        sendSuccess: true,
      });
    } else {
      this.setState({ ...this.state, sendError: true });
    }
  }

  tableOrBox(process: any) {
    if (this.props.table !== undefined) {
      return <OcrProcessTable process={process} />;
    } else {
      return <OcrProcessBoxList process={process} />;
    }
  }

  render() {
    return (
      <Query query={GET_PROCESS} variables={{ number: this.props.number }}>
        {({ loading, error, data }: any) => {
          if (loading) {
            return <LoadingView />;
          }

          if (error) {
            return <ErrorView error={error} />;
          }

          const item = data && data.ocrProcesses && data.ocrProcesses[0];

          if (!item) {
            return <ErrorView error={"No ocrProcess returned"} />;
          }

          const inProgress = data.ocrProcesses[0].sum == 0;
          const inProgressStr = inProgress ? " (in Bearbeitung)" : "";
          const inProgressNote = inProgress
            ? "Hinweis: Belege werden erst angezeigt sobald sie von uns erfasst wurden."
            : "";

          const submissionNote = data.ocrProcesses[0].submissionNote;
          var submissionNoteExists = false;
          if (submissionNote) {
            submissionNoteExists = true;
          }

          if (this.state.subject === "") {
            this.setState({
              ...this.state,
              subject: this.emailQuestionSubject(item.processNumber),
            });
          }

          return (
            <>
              <MessageEditorModal
                onHide={() =>
                  this.setState({ ...this.state, messageEditorIsOpen: false })
                }
                show={this.state.messageEditorIsOpen}
                subject={this.state.subject}
                updateSubject={undefined}
                updateMessageText={this.updateMessageText}
                message={this.state.message}
                submitMessage={this.submitMessage}
                success={this.state.sendSuccess}
                error={this.state.sendError}
              />
              <h3>
                Vorgang {item.processNumber}
                {inProgressStr}
              </h3>
              <p>{inProgressNote}</p>
              {submissionNoteExists && (
                <>
                  <h5>Einreichungsnotiz</h5>
                  <p>{submissionNote}</p>
                </>
              )}
              {this.tableOrBox(data.ocrProcesses[0])}
              {!inProgress && (
                <>
                  <h3>Summen</h3>
                  <div className="berechnung-wrapper box-standard">
                    <table className="table-hover table-borderless">
                      <tbody>
                        <tr>
                          <td>
                            <strong>Summe aller Belege</strong>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {data.ocrProcesses[0].sum.toFixed(2)}&nbsp;€
                          </td>
                        </tr>
                        <tr>
                          <td
                            style={{
                              borderTop: "1px solid black",
                              paddingRight: "1em",
                            }}
                          >
                            <strong>Erstattung Versicherungsunternehmen</strong>
                          </td>
                          <td
                            style={{
                              borderTop: "1px solid black",
                              textAlign: "right",
                            }}
                          >
                            {data.ocrProcesses[0].erstattungVu.toFixed(2)}
                            &nbsp;€
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Erstattung Beihilfe</strong>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {data.ocrProcesses[0].erstattungBh.toFixed(2)}
                            &nbsp;€
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <strong>Summe erfolgter Nacherstattungen</strong>
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              color: `${
                                data.ocrProcesses[0].sew > 0 ? "green" : "black"
                              }`,
                            }}
                          >
                            {data.ocrProcesses[0].sew.toFixed(2)}
                            &nbsp;€
                          </td>
                        </tr>
                        <tr>
                          <td style={{ borderTop: "1px solid black" }}>
                            <strong>Eigenanteil</strong>
                          </td>
                          <td
                            style={{
                              borderTop: "1px solid black",
                              textAlign: "right",
                            }}
                          >
                            {(
                              data.ocrProcesses[0].sum -
                              data.ocrProcesses[0].erstattungVu -
                              data.ocrProcesses[0].erstattungBh -
                              data.ocrProcesses[0].sew
                            ).toFixed(2)}
                            &nbsp;€
                          </td>
                        </tr>
                        <tr>
                          <td>&nbsp;</td>
                          <td>&nbsp;</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <Button
                size="lg"
                variant="outline-primary"
                onClick={() => this.composeMessage()}
                style={{ float: "right", marginRight: "2em" }}
              >
                Frage zum Vorgang
              </Button>
              {Array.isArray(data.ocrProcesses[0].attachments) &&
                data.ocrProcesses[0].attachments.length > 0 && (
                  <>
                    <h3>Anhänge zum Abrechnungsvorgang</h3>
                    <div className="document-list">
                      {data.ocrProcesses[0].attachments.map(
                        (attachment: any, idx: number) => (
                          <div key={`attli-${idx}`} className="list-item">
                            {attachment.images.length > 0 && (
                              <AttachmentLightbox
                                likey={`att-${idx}`}
                                images={attachment.images}
                                photoIndex={0}
                              />
                            )}
                            {attachment.images.length == 0 && (
                              <Image
                                className="img-fluid"
                                style={{ border: "1px solid #eeeeee" }}
                                src={unknown_file}
                              />
                            )}
                            <div className="txt-wrapper">
                              <span>{attachment.name}</span>
                              <a href={attachment.url}>DOWNLOAD</a>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </>
                )}
            </>
          );
        }}
      </Query>
    );
  }
}
